import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppCriteria, AppResultInfo, AppSkinProps } from '../../../../types/decisions';
import { getFieldName } from '../../../../utils';
import {
    AppResultsProps,
    getRankLabel,
    renderSpecByType,
    ResultsPagination,
    useResultActivityTracking,
} from '../AppResultsShared/AppResultsShared';
import './AppResultsV2.scss';

type ViewKey = 'cards' | 'cardsPlain' | 'list' | 'listPlain' | 'tiles' | 'spotlight';

const VIEW_STORAGE_KEY = 'appResultsV2View';

const VIEWS: { key: ViewKey; label: string; icon: string }[] = [
    {
        key: 'cards',
        label: 'Cards with details',
        icon: 'M3 3.5h8v6H3Z M13 3.5h8v6h-8Z M3 12h8M13 12h8M3 14.5h5M13 14.5h5 M3 17.5h8v3H3Z M13 17.5h8v3h-8Z',
    },
    {
        key: 'cardsPlain',
        label: 'Cards without details',
        icon: 'M4 4h7v7H4Z M13 4h7v7h-7Z M4 13h7v7H4Z M13 13h7v7h-7Z',
    },
    {
        key: 'list',
        label: 'List with details',
        icon: 'M3 4.5h5v5H3Z M10.5 5.5h10.5M10.5 8.5h7 M3 14.5h5v5H3Z M10.5 15.5h10.5M10.5 18.5h7',
    },
    {
        key: 'listPlain',
        label: 'List without details',
        icon: 'M3 5h3v3H3Z M9 6.5h12 M3 10.5h3v3H3Z M9 12h12 M3 16h3v3H3Z M9 17.5h12',
    },
    {
        key: 'tiles',
        label: 'Small tiles',
        icon: 'M4 4h4v4H4Z M10 4h4v4h-4Z M16 4h4v4h-4Z M4 10h4v4H4Z M10 10h4v4h-4Z M16 10h4v4h-4Z M4 16h4v4H4Z M10 16h4v4h-4Z M16 16h4v4h-4Z',
    },
    {
        key: 'spotlight',
        label: 'Top result spotlight',
        icon: 'M3 4.5h5v5H3Z M10.5 5.5h10.5M10.5 8.5h7 M3 13.5h18M3 17.5h18',
    },
];

const ResultFactChips = ({
    nonTechnicalFields,
    allowedFieldsIds,
}: {
    nonTechnicalFields: AppResultInfo['nonTechnicalFields'];
    allowedFieldsIds: string[];
}) => (
    <div className="app-results-v2-facts">
        {nonTechnicalFields
        .filter(field => allowedFieldsIds.includes(field.id))
        .map(field => (
            <span className="app-results-v2-fact" key={field.id}>
                    <span className="app-results-v2-fact-label">{getFieldName(field.name)}</span>
                    <span className="app-results-v2-fact-value">{renderSpecByType(field.value, field.type)}</span>
                </span>
        ))}
    </div>
);

const ScoreBadge = ({ rank, isTop, config }: {
    rank: AppResultInfo['rank'];
    isTop: boolean;
    config?: AppCriteria['config'];
}) => (
    <span className={`app-results-v2-score ${isTop ? 'is-top' : ''}`}>
        {getRankLabel(rank, config?.scoreDisplay, config?.scoreRanges)}
    </span>
);

const SaveButton = ({
    saved,
    onToggle,
    label,
    size = 'md',
}: {
    saved: boolean;
    onToggle: () => void;
    label: string;
    size?: 'sm' | 'md' | 'lg';
}) => (
    <button
        type="button"
        className={`app-results-v2-icon-btn app-results-v2-save ${saved ? 'is-saved' : ''} ${size}`}
        onClick={onToggle}
        aria-pressed={saved}
        aria-label={label}
        title={label}
    >
        <svg width="16" height="16" viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor"
             strokeWidth={1.8} strokeLinejoin="round">
            <path d="M12 21.3 3.4 12.9A5.4 5.4 0 0 1 11 5.3l1 1 1-1a5.4 5.4 0 0 1 7.6 7.6Z"/>
        </svg>
    </button>
);

const RerankButton = ({
    onClick,
    label,
    size = 'md',
}: {
    onClick: () => void;
    label: string;
    size?: 'sm' | 'md' | 'lg';
}) => (
    <button
        type="button"
        className={`app-results-v2-icon-btn ${size}`}
        onClick={onClick}
        aria-label={label}
        title={label}
    >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
             strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5M6 11l6-6 6 6"/>
        </svg>
    </button>
);

const CtaButton = ({
    label,
    onClick,
}: {
    label: string;
    onClick: () => void;
}) => (
    <button type="button" className="app-results-v2-cta" onClick={onClick}>
        {label}
    </button>
);

const getStoredView = (): ViewKey => {
    try {
        const stored = localStorage.getItem(VIEW_STORAGE_KEY) as ViewKey | null;

        if (stored && VIEWS.some(v => v.key === stored)) {
            return stored;
        }
    } catch (e) {
        // ignore unavailable storage
    }

    return 'cards';
};

const AppResultsV2 = ({
    pageInfo,
    results,
    onPageChange,
    onRerank,
    onRerankMany,
    shouldShowRankByManyCheckbox,
    selectedProductIds,
    setSelectedProductIds,
    allowedFieldsIds,
    criteriaData,
}: Omit<AppResultsProps, 'currentPage'> & {
    pageInfo: AppSkinProps['pageInfo'];
    onPageChange: AppSkinProps['onPageChange'];
    criteriaData?: AppSkinProps['criteriaData'];
    criteriaConstraints?: AppSkinProps['criteriaConstraints'];
}) => {
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const [ view, setView ] = useState<ViewKey>(getStoredView);

    const handleViewChange = (key: ViewKey) => {
        setView(key);

        try {
            localStorage.setItem(VIEW_STORAGE_KEY, key);
        } catch (e) {
            // ignore unavailable storage
        }
    };

    const onSaveToggle = (id: string) => {
        if (selectedProductIds.includes(id)) {
            setSelectedProductIds(selectedProductIds.filter(pid => pid !== id));
        } else {
            setSelectedProductIds([ ...selectedProductIds, id ]);
        }
    };

    const { onProductLinkClick, onProductActionClick } = useResultActivityTracking(criteriaData);

    if (results.length === 0) {
        return <div className="no-results">No products found.</div>;
    }

    const isGrid = view === 'cards' || view === 'cardsPlain';
    const isList = view === 'list' || view === 'listPlain' || view === 'spotlight';
    const isTiles = view === 'tiles';

    return (
        <div className="app-results-v2-container">
            <div className="app-results-v2-header">
                <h3>Results <span>({pageInfo.total})</span></h3>

                <div className="app-results-v2-view-switcher" role="group" aria-label="Results view">
                    {VIEWS.map(v => (
                        <button
                            key={v.key}
                            type="button"
                            className={`app-results-v2-view-btn ${view === v.key ? 'active' : ''}`}
                            aria-pressed={view === v.key}
                            aria-label={v.label}
                            title={v.label}
                            onClick={() => handleViewChange(v.key)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
                                <path d={v.icon}/>
                            </svg>
                        </button>
                    ))}
                </div>
            </div>

            {isGrid && (
                <div className="app-results-v2-grid">
                    {results.map((result, index) => {
                        const name = getFieldName(result.data.name);
                        const isTop = index === 0 && pageInfo.current_page === 1;
                        const saved = selectedProductIds.includes(result.id);
                        const productLink = result.data.productlink?.value;
                        const actionLink = result.data.actionlink?.value;
                        const actionCTAName = result.data.actionbuttontext?.value;

                        return (
                            <article className={`app-results-v2-card ${isTop ? 'is-top' : ''}`} key={result.id}>
                                <div
                                    className={`app-results-v2-card-image ${productLink ? 'product-link' : ''}`}
                                    style={{ backgroundImage: `url('${result.icon}')` }}
                                    onClick={() => productLink && onProductLinkClick(result.id, productLink)}
                                >
                                    <ScoreBadge rank={result.rank} isTop={isTop} config={criteriaData?.config}/>
                                    <SaveButton saved={saved} onToggle={() => onSaveToggle(result.id)}
                                                label={(saved ? 'Remove ' : 'Save ') + name}/>
                                </div>

                                <div className="app-results-v2-card-row">
                                    <h4
                                        className={`truncate ${productLink ? 'product-link' : ''}`}
                                        title={name}
                                        onClick={() => productLink && onProductLinkClick(result.id, productLink)}
                                    >
                                        {name}
                                    </h4>
                                    {!isTop && (
                                        <RerankButton onClick={() => onRerank(result.id, dataset, true)}
                                                      label={'Make ' + name + ' #1'}/>
                                    )}
                                </div>

                                {view === 'cards' && (
                                    <ResultFactChips nonTechnicalFields={result.nonTechnicalFields}
                                                     allowedFieldsIds={allowedFieldsIds}/>
                                )}

                                {actionLink && actionCTAName && (
                                    <CtaButton label={actionCTAName}
                                               onClick={() => onProductActionClick(result.id, actionLink)}/>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            {isList && (
                <div className={`app-results-v2-list ${view === 'listPlain' ? 'is-plain' : ''}`}>
                    {results.map((result, index) => {
                        const name = getFieldName(result.data.name);
                        const isTop = index === 0 && pageInfo.current_page === 1;
                        const saved = selectedProductIds.includes(result.id);
                        const productLink = result.data.productlink?.value;
                        const actionLink = result.data.actionlink?.value;
                        const actionCTAName = result.data.actionbuttontext?.value;

                        const isRowPlain = view === 'listPlain' || (view === 'spotlight' && !isTop);
                        const showFacts = view === 'list' || (view === 'spotlight' && isTop);

                        return (
                            <article
                                className={`app-results-v2-row ${isTop ? 'is-top' : ''} ${isRowPlain ? 'is-plain' : ''}`}
                                key={result.id}>
                                <div className="app-results-v2-row-main">
                                    <SaveButton saved={saved} onToggle={() => onSaveToggle(result.id)}
                                                label={(saved ? 'Remove ' : 'Save ') + name} size="lg"/>

                                    <div
                                        className={`app-results-v2-row-image ${productLink ? 'product-link' : ''}`}
                                        style={{ backgroundImage: `url('${result.icon}')` }}
                                        onClick={() => productLink && onProductLinkClick(result.id, productLink)}
                                    >
                                        <ScoreBadge rank={result.rank} isTop={isTop} config={criteriaData?.config}/>
                                    </div>

                                    <div className="app-results-v2-row-body">
                                        <h4
                                            className={`truncate ${productLink ? 'product-link' : ''}`}
                                            title={name}
                                            onClick={() => productLink && onProductLinkClick(result.id, productLink)}
                                        >
                                            {name}
                                        </h4>

                                        {showFacts && (
                                            <ResultFactChips nonTechnicalFields={result.nonTechnicalFields}
                                                             allowedFieldsIds={allowedFieldsIds}/>
                                        )}
                                    </div>

                                    {!isTop && (
                                        <RerankButton onClick={() => onRerank(result.id, dataset, true)}
                                                      label={'Make ' + name + ' #1'} size="lg"/>
                                    )}
                                </div>

                                {actionLink && actionCTAName && (
                                    <CtaButton label={actionCTAName}
                                               onClick={() => onProductActionClick(result.id, actionLink)}/>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            {isTiles && (
                <div className="app-results-v2-tiles">
                    {results.map((result, index) => {
                        const name = getFieldName(result.data.name);
                        const isTop = index === 0 && pageInfo.current_page === 1;
                        const saved = selectedProductIds.includes(result.id);

                        return (
                            <article className={`app-results-v2-tile ${isTop ? 'is-top' : ''}`} key={result.id}>
                                <div className="app-results-v2-tile-image"
                                     style={{ backgroundImage: `url('${result.icon}')` }}>
                                    <ScoreBadge rank={result.rank} isTop={isTop} config={criteriaData?.config}/>
                                    <SaveButton saved={saved} onToggle={() => onSaveToggle(result.id)}
                                                label={(saved ? 'Remove ' : 'Save ') + name} size="sm"/>
                                    {!isTop && (
                                        <RerankButton onClick={() => onRerank(result.id, dataset, true)}
                                                      label={'Make ' + name + ' #1'} size="sm"/>
                                    )}
                                </div>
                                <div className="app-results-v2-tile-name truncate" title={name}>{name}</div>
                            </article>
                        );
                    })}
                </div>
            )}

            {shouldShowRankByManyCheckbox && (
                <button
                    type="button"
                    className="app-results-v2-infer-btn"
                    style={{ right: selectedProductIds.length > 0 ? 16 : -120 }}
                    onClick={() => onRerankMany(dataset, true)}
                >
                    Infer
                </button>
            )}

            <ResultsPagination pageInfo={pageInfo} dataset={dataset} onPageChange={onPageChange}/>
        </div>
    );
};

export default AppResultsV2;
